'use client';
import React, { useState, useEffect, useCallback } from 'react';
import AdSpot from '@/components/AdSpot';
import {
  Newspaper, TrendingUp, Cpu, FlaskConical, Star, Heart,
  Globe, Briefcase, RefreshCw, ExternalLink, Clock, AlertCircle,
  CloudSun, BarChart2, ChevronUp, ChevronDown, X, ArrowLeft, Share2
} from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────────────────
interface Article {
  source: { id: string | null; name: string };
  author: string | null;
  title: string;
  description: string | null;
  url: string;
  urlToImage: string | null;
  publishedAt: string;
  content: string | null;
}

interface StockItem {
  symbol: string;
  name: string;
  price: string;
  change: string;
  up: boolean;
}

// ─── Constants ────────────────────────────────────────────────────────────────
const CATEGORIES = [
  { id: 'current-affairs', label: 'Current Affairs', icon: <Globe size={14} /> },
  { id: 'politics', label: 'Politics', icon: <Newspaper size={14} /> },
  { id: 'business', label: 'Business', icon: <Briefcase size={14} /> },
  { id: 'technology', label: 'Technology', icon: <Cpu size={14} /> },
  { id: 'ai', label: 'AI', icon: <TrendingUp size={14} /> },
  { id: 'science', label: 'Science', icon: <FlaskConical size={14} /> },
  { id: 'celebrity', label: 'Celebrity', icon: <Star size={14} /> },
  { id: 'human-interest', label: 'Human Interest', icon: <Heart size={14} /> },
];

const MOCK_STOCKS: StockItem[] = [
  { symbol: 'JSE', name: 'JSE All Share', price: '79,420', change: '+1.2%', up: true },
  { symbol: 'NGX', name: 'NGX All Share', price: '98,310', change: '+0.8%', up: true },
  { symbol: 'NSE20', name: 'NSE 20 (Kenya)', price: '1,842', change: '-0.3%', up: false },
  { symbol: 'EGX30', name: 'EGX 30 (Egypt)', price: '26,540', change: '+2.1%', up: true },
  { symbol: 'BRVM', name: 'BRVM Composite', price: '218', change: '-0.6%', up: false },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function truncate(str: string | null, len: number): string {
  if (!str) return '';
  return str.length > len ? str.slice(0, len) + '…' : str;
}

// ─── Interstitial Ad Modal ────────────────────────────────────────────────────
function InterstitialAdModal({ targetUrl, sourceName, onClose }: {
  targetUrl: string;
  sourceName: string;
  onClose: () => void;
}) {
  const [countdown, setCountdown] = useState(5);
  const [canSkip, setCanSkip] = useState(false);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  useEffect(() => {
    if (countdown <= 0) {
      setCanSkip(true);
      return;
    }
    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  const handleRedirect = () => {
    onClose();
    window.open(targetUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90 backdrop-blur-sm p-4">
      <div className="relative w-full max-w-lg bg-[rgb(10,10,10)] border border-[rgb(38,38,38)] rounded-2xl overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-[rgb(38,38,38)] bg-[rgb(15,15,15)]">
          <span className="text-xs text-[rgb(115,115,115)]">Advertisement · Redirecting to <span className="text-white">{sourceName}</span></span>
          <button
            onClick={canSkip ? handleRedirect : undefined}
            disabled={!canSkip}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-600 transition-all ${
              canSkip
                ? 'bg-red-600 hover:bg-red-700 text-white cursor-pointer' :'bg-[rgb(38,38,38)] text-[rgb(115,115,115)] cursor-not-allowed'
            }`}
          >
            {canSkip ? (
              <>Skip <ExternalLink size={11} /></>
            ) : (
              <>Skip in {countdown}s</>
            )}
          </button>
        </div>

        {/* Ad content */}
        <div className="p-5 flex flex-col items-center gap-4">
          <AdSpot slot="1234567890" format="rectangle" className="w-full" />

          {/* Progress bar */}
          <div className="w-full">
            <div className="h-1 bg-[rgb(38,38,38)] rounded-full overflow-hidden">
              <div
                className="h-full bg-red-600 rounded-full transition-all duration-1000 ease-linear"
                style={{ width: `${((5 - countdown) / 5) * 100}%` }}
              />
            </div>
            <p className="text-[10px] text-[rgb(64,64,64)] mt-1.5 text-center">
              {canSkip ? 'You can now continue to the article' : `You can skip this ad in ${countdown} second${countdown !== 1 ? 's' : ''}`}
            </p>
          </div>

          {canSkip && (
            <button
              onClick={handleRedirect}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white text-sm font-600 rounded-lg transition-all"
            >
              <ExternalLink size={14} />
              Continue to {sourceName}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Article Reader Modal ─────────────────────────────────────────────────────
function ArticleReaderModal({ article, onClose }: { article: Article; onClose: () => void }) {
  const [imgError, setImgError] = useState(false);
  const [fullParagraphs, setFullParagraphs] = useState<string[] | null>(null);
  const [fetchingFull, setFetchingFull] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [showInterstitial, setShowInterstitial] = useState(false);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  // Fetch full article content from our proxy
  useEffect(() => {
    let cancelled = false;
    setFetchingFull(true);
    setFetchError(false);
    setFullParagraphs(null);

    fetch(`/api/article-content?url=${encodeURIComponent(article.url)}`)
      .then((res) => res.json())
      .then((data) => {
        if (cancelled) return;
        if (data.paragraphs && data.paragraphs.length > 0) {
          setFullParagraphs(data.paragraphs);
        } else {
          setFetchError(true);
        }
      })
      .catch(() => {
        if (!cancelled) setFetchError(true);
      })
      .finally(() => {
        if (!cancelled) setFetchingFull(false);
      });

    return () => { cancelled = true; };
  }, [article.url]);

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: article.title, url: article.url });
      } catch {}
    } else {
      navigator.clipboard.writeText(article.url).catch(() => {});
    }
  };

  const handleExternalClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowInterstitial(true);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/80 backdrop-blur-sm overflow-y-auto"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {showInterstitial && (
        <InterstitialAdModal
          targetUrl={article.url}
          sourceName={article.source.name}
          onClose={() => setShowInterstitial(false)}
        />
      )}
      <div className="relative w-full max-w-3xl mx-auto my-4 sm:my-8 bg-[rgb(10,10,10)] border border-[rgb(38,38,38)] rounded-2xl overflow-hidden shadow-2xl">
        {/* Top bar */}
        <div className="sticky top-0 z-10 flex items-center justify-between px-4 py-3 bg-[rgb(10,10,10)]/95 backdrop-blur-sm border-b border-[rgb(38,38,38)]">
          <button
            onClick={onClose}
            className="flex items-center gap-1.5 text-[rgb(163,163,163)] hover:text-white text-xs transition-colors"
          >
            <ArrowLeft size={14} />
            Back to News
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={handleShare}
              className="flex items-center gap-1.5 px-2.5 py-1.5 bg-[rgb(24,24,24)] border border-[rgb(38,38,38)] hover:border-red-600/50 text-[rgb(163,163,163)] hover:text-white text-xs rounded-lg transition-all"
            >
              <Share2 size={12} />
              Share
            </button>
            <button
              onClick={onClose}
              className="p-1.5 text-[rgb(115,115,115)] hover:text-white transition-colors"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Hero image */}
        {article.urlToImage && !imgError && (
          <div className="relative h-56 sm:h-72 bg-[rgb(24,24,24)] overflow-hidden">
            <img
              src={article.urlToImage}
              alt={article.title}
              className="w-full h-full object-cover"
              onError={() => setImgError(true)}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[rgb(10,10,10)] via-transparent to-transparent" />
          </div>
        )}

        {/* Article body */}
        <div className="px-5 sm:px-8 py-6 space-y-4">
          {/* Source + time */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="bg-red-600 text-white text-[10px] font-700 px-2 py-0.5 rounded-full">
              {article.source.name}
            </span>
            {article.author && (
              <span className="text-[10px] text-[rgb(115,115,115)]">by {article.author}</span>
            )}
            <span className="flex items-center gap-1 text-[10px] text-[rgb(64,64,64)] ml-auto">
              <Clock size={10} />
              {timeAgo(article.publishedAt)}
            </span>
          </div>

          {/* Title */}
          <h1 className="text-lg sm:text-2xl font-800 text-white leading-snug">
            {article.title}
          </h1>

          {/* Description */}
          {article.description && (
            <p className="text-sm text-[rgb(163,163,163)] leading-relaxed border-l-2 border-red-600 pl-4">
              {article.description}
            </p>
          )}

          {/* Full content area */}
          {fetchingFull ? (
            <div className="space-y-3 animate-pulse">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className={`h-3 rounded bg-[rgb(38,38,38)] ${i % 3 === 2 ? 'w-3/4' : 'w-full'}`} />
              ))}
            </div>
          ) : fullParagraphs && fullParagraphs.length > 0 ? (
            <div className="text-sm text-[rgb(200,200,200)] leading-relaxed space-y-4">
              {fullParagraphs.map((para, i) => (
                <p key={i}>{para}</p>
              ))}
            </div>
          ) : fetchError ? (
            <div className="text-sm text-[rgb(115,115,115)] leading-relaxed bg-[rgb(18,18,18)] border border-[rgb(38,38,38)] rounded-lg p-4">
              <p className="font-600 text-white mb-1">Content unavailable</p>
              <p>This article&apos;s content could not be loaded directly — the publisher may restrict access. You can read the full story at the source using the link below.</p>
            </div>
          ) : null}

          {/* CTA to source — only at the bottom */}
          <div className="pt-4 border-t border-[rgb(38,38,38)]">
            <p className="text-[10px] text-[rgb(64,64,64)] mb-3">
              Original article published by <span className="text-[rgb(115,115,115)]">{article.source.name}</span>
            </p>
            <a
              href={article.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleExternalClick}
              className={`inline-flex items-center gap-2 px-4 py-2.5 border rounded-lg transition-all text-sm font-600 ${
                fetchError
                  ? 'bg-red-600 hover:bg-red-700 border-red-600 text-white' :'bg-[rgb(24,24,24)] border-[rgb(38,38,38)] hover:border-red-600/50 hover:bg-[rgb(30,30,30)] text-white'
              }`}
            >
              <ExternalLink size={14} />
              {fetchError ? 'Read Full Story' : `Visit original story on ${article.source.name}`}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Skeleton Card ────────────────────────────────────────────────────────────
function SkeletonCard() {
  return (
    <div className="bg-[rgb(15,15,15)] border border-[rgb(38,38,38)] rounded-xl overflow-hidden">
      <div className="skeleton h-44 w-full" />
      <div className="p-3 space-y-2">
        <div className="skeleton h-3 w-1/3 rounded" />
        <div className="skeleton h-4 w-full rounded" />
        <div className="skeleton h-4 w-4/5 rounded" />
        <div className="skeleton h-3 w-1/2 rounded" />
      </div>
    </div>
  );
}

// ─── News Card ────────────────────────────────────────────────────────────────
function NewsCard({ article, onRead }: { article: Article; onRead: (a: Article) => void }) {
  const [imgError, setImgError] = useState(false);

  return (
    <button
      onClick={() => onRead(article)}
      className="group bg-[rgb(15,15,15)] border border-[rgb(38,38,38)] rounded-xl overflow-hidden hover:border-red-600/50 transition-all duration-200 card-hover flex flex-col text-left w-full"
    >
      {/* Thumbnail */}
      <div className="relative h-44 bg-[rgb(24,24,24)] overflow-hidden flex-shrink-0">
        {article.urlToImage && !imgError ? (
          <img
            src={article.urlToImage}
            alt={article.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            onError={() => setImgError(true)}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Newspaper size={32} className="text-[rgb(64,64,64)]" />
          </div>
        )}
        <div className="absolute top-2 left-2">
          <span className="bg-black/70 backdrop-blur-sm text-[10px] text-[rgb(163,163,163)] px-2 py-0.5 rounded-full border border-[rgb(38,38,38)]">
            {article.source.name}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="p-3 flex flex-col flex-1">
        <h3 className="text-sm font-600 text-white leading-snug mb-1.5 group-hover:text-red-400 transition-colors line-clamp-2">
          {article.title}
        </h3>
        {article.description && (
          <p className="text-xs text-[rgb(115,115,115)] leading-relaxed mb-2 line-clamp-2 flex-1">
            {truncate(article.description, 120)}
          </p>
        )}
        <div className="flex items-center justify-between mt-auto pt-1">
          <span className="flex items-center gap-1 text-[10px] text-[rgb(64,64,64)]">
            <Clock size={10} />
            {timeAgo(article.publishedAt)}
          </span>
          <span className="text-[10px] text-red-500 font-600 group-hover:text-red-400 transition-colors">Read →</span>
        </div>
      </div>
    </button>
  );
}

// ─── Featured Card (large) ────────────────────────────────────────────────────
function FeaturedCard({ article, onRead }: { article: Article; onRead: (a: Article) => void }) {
  const [imgError, setImgError] = useState(false);

  return (
    <button
      onClick={() => onRead(article)}
      className="group relative bg-[rgb(15,15,15)] border border-[rgb(38,38,38)] rounded-xl overflow-hidden hover:border-red-600/50 transition-all duration-200 col-span-full md:col-span-2 text-left w-full"
    >
      <div className="relative h-64 sm:h-72 bg-[rgb(24,24,24)] overflow-hidden">
        {article.urlToImage && !imgError ? (
          <img
            src={article.urlToImage}
            alt={article.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            onError={() => setImgError(true)}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Newspaper size={48} className="text-[rgb(64,64,64)]" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <span className="inline-block bg-red-600 text-white text-[10px] font-700 px-2 py-0.5 rounded-full mb-2">
            {article.source.name}
          </span>
          <h2 className="text-base sm:text-lg font-700 text-white leading-snug group-hover:text-red-300 transition-colors line-clamp-2">
            {article.title}
          </h2>
          {article.description && (
            <p className="text-xs text-[rgb(163,163,163)] mt-1 line-clamp-2 hidden sm:block">
              {truncate(article.description, 150)}
            </p>
          )}
          <span className="flex items-center gap-1 text-[10px] text-[rgb(115,115,115)] mt-2">
            <Clock size={10} />
            {timeAgo(article.publishedAt)}
          </span>
        </div>
      </div>
    </button>
  );
}

// ─── Weather Widget ───────────────────────────────────────────────────────────
function WeatherWidget() {
  const cities = [
    { city: 'Nairobi', temp: '22°C', condition: 'Partly Cloudy', icon: '⛅' },
    { city: 'Lagos', temp: '31°C', condition: 'Sunny', icon: '☀️' },
    { city: 'Cairo', temp: '28°C', condition: 'Clear', icon: '🌤️' },
    { city: 'Johannesburg', temp: '18°C', condition: 'Cloudy', icon: '🌥️' },
    { city: 'Accra', temp: '29°C', condition: 'Humid', icon: '🌦️' },
  ];

  return (
    <div className="bg-[rgb(15,15,15)] border border-[rgb(38,38,38)] rounded-xl p-4">
      <div className="flex items-center gap-2 mb-3">
        <CloudSun size={16} className="text-yellow-400" />
        <h3 className="text-sm font-700 text-white">African Weather</h3>
      </div>
      <div className="space-y-2">
        {cities.map((c) => (
          <div key={c.city} className="flex items-center justify-between py-1.5 border-b border-[rgb(38,38,38)] last:border-0">
            <div className="flex items-center gap-2">
              <span className="text-base">{c.icon}</span>
              <div>
                <p className="text-xs font-600 text-white">{c.city}</p>
                <p className="text-[10px] text-[rgb(115,115,115)]">{c.condition}</p>
              </div>
            </div>
            <span className="text-sm font-700 text-white">{c.temp}</span>
          </div>
        ))}
      </div>
      <p className="text-[9px] text-[rgb(64,64,64)] mt-2">Indicative data · Updated hourly</p>
    </div>
  );
}

// ─── Stocks Widget ────────────────────────────────────────────────────────────
function StocksWidget() {
  return (
    <div className="bg-[rgb(15,15,15)] border border-[rgb(38,38,38)] rounded-xl p-4">
      <div className="flex items-center gap-2 mb-3">
        <BarChart2 size={16} className="text-green-400" />
        <h3 className="text-sm font-700 text-white">African Markets</h3>
      </div>
      <div className="space-y-2">
        {MOCK_STOCKS.map((s) => (
          <div key={s.symbol} className="flex items-center justify-between py-1.5 border-b border-[rgb(38,38,38)] last:border-0">
            <div>
              <p className="text-xs font-700 text-white">{s.symbol}</p>
              <p className="text-[10px] text-[rgb(115,115,115)]">{s.name}</p>
            </div>
            <div className="text-right">
              <p className="text-xs font-600 text-white">{s.price}</p>
              <p className={`text-[10px] font-600 flex items-center gap-0.5 justify-end ${s.up ? 'text-green-400' : 'text-red-400'}`}>
                {s.up ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
                {s.change}
              </p>
            </div>
          </div>
        ))}
      </div>
      <p className="text-[9px] text-[rgb(64,64,64)] mt-2">Indicative data · Not financial advice</p>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function NewsAggregatorClient() {
  const [activeCategory, setActiveCategory] = useState('current-affairs');
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState<Article | null>(null);

  const fetchNews = useCallback(async (category: string, pageNum: number, append = false) => {
    if (pageNum === 1) setLoading(true);
    else setLoadingMore(true);
    setError(null);

    try {
      const res = await fetch(`/api/news?category=${category}&page=${pageNum}&pageSize=20`);
      const data = await res.json();

      if (!res.ok) {
        setError(data.error || 'Failed to load news');
        if (!append) setArticles([]);
        return;
      }

      const newArticles: Article[] = (data.articles || []).filter(
        (a: Article) => a.title && a.title !== '[Removed]'
      );

      if (append) {
        setArticles((prev) => [...prev, ...newArticles]);
      } else {
        setArticles(newArticles);
      }

      setHasMore(newArticles.length === 20);
    } catch {
      setError('Network error — please try again');
      if (!append) setArticles([]);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    setPage(1);
    fetchNews(activeCategory, 1, false);
  }, [activeCategory, fetchNews]);

  const handleLoadMore = () => {
    const next = page + 1;
    setPage(next);
    fetchNews(activeCategory, next, true);
  };

  const handleRefresh = () => {
    setPage(1);
    fetchNews(activeCategory, 1, false);
  };

  // Inject ad every 6 cards
  const renderGrid = () => {
    if (loading) {
      return Array.from({ length: 8 }).map((_, i) => <SkeletonCard key={i} />);
    }

    const items: React.ReactNode[] = [];
    articles.forEach((article, idx) => {
      if (idx === 0) {
        items.push(<FeaturedCard key={`featured-${article.url}`} article={article} onRead={setSelectedArticle} />);
      } else {
        items.push(<NewsCard key={`card-${article.url}-${idx}`} article={article} onRead={setSelectedArticle} />);
      }
      // Insert ad after every 6th card (after index 5, 11, 17…)
      if ((idx + 1) % 6 === 0) {
        items.push(
          <div key={`ad-${idx}`} className="col-span-full">
            <AdSpot slot="9876543210" format="horizontal" className="w-full" />
          </div>
        );
      }
    });
    return items;
  };

  return (
    <div className="space-y-4">
      {/* Article Reader Modal */}
      {selectedArticle && (
        <ArticleReaderModal article={selectedArticle} onClose={() => setSelectedArticle(null)} />
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl font-800 text-white">Top Stories</h1>
          <p className="text-xs text-[rgb(115,115,115)] mt-0.5">Africa &amp; World News · Powered by NewsAPI</p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={loading}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-[rgb(24,24,24)] border border-[rgb(38,38,38)] hover:border-red-600/50 text-[rgb(163,163,163)] hover:text-white text-xs rounded-lg transition-all disabled:opacity-50"
        >
          <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Category Tabs */}
      <div className="flex gap-1.5 overflow-x-auto scrollbar-hide pb-1">
        {CATEGORIES.map((cat) => (
          <button
            key={cat.id}
            onClick={() => setActiveCategory(cat.id)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-600 whitespace-nowrap transition-all flex-shrink-0 ${
              activeCategory === cat.id
                ? 'bg-red-600 text-white' :'bg-[rgb(24,24,24)] border border-[rgb(38,38,38)] text-[rgb(163,163,163)] hover:text-white hover:border-red-600/50'
            }`}
          >
            {cat.icon}
            {cat.label}
          </button>
        ))}
      </div>

      {/* Top Banner Ad */}
      <AdSpot slot="1122334455" format="horizontal" className="w-full" />

      {/* Main Layout: Grid + Sidebar */}
      <div className="flex flex-col xl:flex-row gap-6">
        {/* News Grid */}
        <div className="flex-1 min-w-0">
          {error ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <AlertCircle size={36} className="text-red-500 mb-3" />
              <p className="text-sm font-600 text-white mb-1">Failed to load news</p>
              <p className="text-xs text-[rgb(115,115,115)] mb-4 max-w-xs">{error}</p>
              <button
                onClick={handleRefresh}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm rounded-lg transition-all"
              >
                Try Again
              </button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {renderGrid()}
              </div>

              {/* Load More */}
              {!loading && articles.length > 0 && hasMore && (
                <div className="mt-6 text-center">
                  <button
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                    className="px-6 py-2.5 bg-[rgb(24,24,24)] border border-[rgb(38,38,38)] hover:border-red-600/50 text-white text-sm font-600 rounded-lg transition-all disabled:opacity-50 flex items-center gap-2 mx-auto"
                  >
                    {loadingMore ? (
                      <><RefreshCw size={14} className="animate-spin" /> Loading…</>
                    ) : (
                      'Load More Stories'
                    )}
                  </button>
                </div>
              )}

              {!loading && articles.length === 0 && !error && (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <Newspaper size={36} className="text-[rgb(64,64,64)] mb-3" />
                  <p className="text-sm text-[rgb(115,115,115)]">No articles found for this category.</p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Sidebar */}
        <aside className="xl:w-72 flex-shrink-0 space-y-4">
          <WeatherWidget />
          <AdSpot slot="5566778899" format="rectangle" className="w-full" />
          <StocksWidget />
          <AdSpot slot="6677889900" format="rectangle" className="w-full" />
        </aside>
      </div>
    </div>
  );
}
